import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { Armazon } from './componentes/layout/Armazon';
import { AceptarInvitacion } from './paginas/AceptarInvitacion';
import { AltaDeEmpresa } from './paginas/AltaDeEmpresa';
import { FichaDeArticulo } from './paginas/FichaDeArticulo';
import { Plataforma } from './paginas/Plataforma';
import { Recuperacion } from './paginas/Recuperacion';
import { FichaDeCliente } from './paginas/FichaDeCliente';
import { FichaDeProveedor } from './paginas/FichaDeProveedor';
import { Articulos } from './paginas/Articulos';
import { CamposPersonalizados } from './paginas/CamposPersonalizados';
import { NumeradoresDeComprobante } from './paginas/NumeradoresDeComprobante';
import { Rubros } from './paginas/Rubros';
import { RecalcularPrecios } from './paginas/RecalcularPrecios';
import { Auditoria } from './paginas/Auditoria';
import { Bancos } from './paginas/Bancos';
import { Caja } from './paginas/Caja';
import { Clientes } from './paginas/Clientes';
import { Comisiones } from './paginas/Comisiones';
import { Produccion } from './paginas/Produccion';
import { Conversiones } from './paginas/Conversiones';
import { TiposDeComprobante } from './paginas/TiposDeComprobante';
import { Compras } from './paginas/Compras';
import { Cuentas } from './paginas/Cuentas';
import { Devoluciones } from './paginas/Devoluciones';
import { EstadoDeCuenta } from './paginas/EstadoDeCuenta';
import { Configuracion } from './paginas/Configuracion';
import { Estructura } from './paginas/Estructura';
import { Existencias } from './paginas/Existencias';
import { Gente } from './paginas/Gente';
import { Inicio } from './paginas/Inicio';
import { Mayorista } from './paginas/Mayorista';
import { Mostrador } from './paginas/Mostrador';
import { Pagos } from './paginas/Pagos';
import { Asientos } from './paginas/Asientos';
import { Ejercicios } from './paginas/Ejercicios';
import { Importacion } from './paginas/Importacion';
import { LibroDeIva } from './paginas/LibroDeIva';
import { Libros } from './paginas/Libros';
import { PlanDeCuentas } from './paginas/PlanDeCuentas';
import { Precios } from './paginas/Precios';
import { Proveedores } from './paginas/Proveedores';
import { Sucursales } from './paginas/Sucursales';
import { Ticket } from './paginas/Ticket';

/**
 * Rutas de la aplicación.
 *
 * El armazón es la raíz y decide según el estado de la sesión: sin sesión muestra la
 * entrada, con sesión y sin empresa el alta guiada, y recién con las dos cosas pinta el
 * contenido. Las rutas públicas —aceptar una invitación— se saltean esa decisión, porque
 * a quien llega con un enlace no se le puede pedir que entre primero.
 */
const rutaRaiz = createRootRoute({ component: Armazon });

/**
 * El ayudante conserva la ruta como tipo literal.
 *
 * Sin el genérico, `path` llega como `string` y el árbol pierde las rutas: el enrutador tipa
 * los destinos con lo que conoce, y lo que conoce pasa a ser nada. El síntoma es raro
 * —`<Link to="/caja">` no compila mientras que una variable de tipo `string` sí— y la causa es
 * ésta. Con el genérico, cada enlace mal escrito se detecta al compilar.
 */
const pagina = <R extends string>(ruta: R, component: () => React.ReactElement) =>
  createRoute({ getParentRoute: () => rutaRaiz, path: ruta, component });

const arbol = rutaRaiz.addChildren([
  pagina('/', Inicio),
  pagina('/mostrador', Mostrador),
  pagina('/mayorista', Mayorista),
  pagina('/articulos', Articulos),
  pagina('/articulos/$id', FichaDeArticulo),
  pagina('/rubros', Rubros),
  pagina('/numeradores', NumeradoresDeComprobante),
  pagina('/campos', CamposPersonalizados),
  pagina('/recalcular-precios', RecalcularPrecios),
  pagina('/precios', Precios),
  pagina('/existencias', Existencias),
  pagina('/compras', Compras),
  pagina('/comisiones', Comisiones),
  pagina('/produccion', Produccion),
  pagina('/tipos-de-comprobante', TiposDeComprobante),
  pagina('/conversiones', Conversiones),
  pagina('/proveedores', Proveedores),
  pagina('/proveedores/$id', FichaDeProveedor),
  pagina('/clientes', Clientes),
  pagina('/clientes/$id', FichaDeCliente),
  pagina('/devoluciones', Devoluciones),
  pagina('/cuentas', Cuentas),
  pagina('/pagos', Pagos),
  pagina('/estado-de-cuenta/$terceroId', EstadoDeCuenta),
  pagina('/caja', Caja),
  pagina('/bancos', Bancos),
  pagina('/sucursales', Sucursales),
  pagina('/estructura', Estructura),
  pagina('/gente', Gente),
  pagina('/plan-de-cuentas', PlanDeCuentas),
  pagina('/asientos', Asientos),
  pagina('/libros', Libros),
  pagina('/libro-de-iva', LibroDeIva),
  pagina('/ejercicios', Ejercicios),
  pagina('/importacion', Importacion),
  pagina('/configuracion', Configuracion),
  pagina('/auditoria', Auditoria),
  pagina('/plataforma', Plataforma),
  pagina('/empresas/nueva', AltaDeEmpresa),
  pagina('/t/$token', Ticket),
  pagina('/recuperacion', Recuperacion),
  pagina('/invitacion', () => <AceptarInvitacion tipo="empresa" />),
  pagina('/fundacion', () => <AceptarInvitacion tipo="fundacion" />),
]);

/**
 * Rutas que no piden sesión. El ticket entra acá porque su QR lo abre cualquiera: quien lo
 * escanea tiene el papel en la mano y ve ese ticket, y nada más.
 */
export const RUTAS_PUBLICAS = ['/invitacion', '/fundacion', '/recuperacion'];

/** El ticket lleva el token en la ruta, así que se reconoce por prefijo. */
export const PREFIJOS_PUBLICOS = ['/t/'];

export const enrutador = createRouter({ routeTree: arbol, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof enrutador;
  }
}
